import { useTranslation } from 'react-i18next';

import type { FilterOption, VarianceFilters } from '../../types';
import { FilterChip } from '@/shared/ui/molecules/FilterChip';
import './ReportFilters.styl';

type Props = { filters: VarianceFilters; onChange: (next: VarianceFilters) => void; sprints: FilterOption[]; members: FilterOption[]; tags: string[] };

/** Select-backed chips: the same look as the design, keyboard-accessible, no custom menus. */
export function ReportFilters({ filters, onChange, sprints, members, tags }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'insights.filters' });
  const patch = (p: { [K in keyof VarianceFilters]?: VarianceFilters[K] | undefined }) => {
    const next: VarianceFilters = { ...filters, ...p } as VarianceFilters;
    (Object.keys(next) as (keyof VarianceFilters)[]).forEach((k) => {
      if (next[k] === undefined || next[k] === '' || next[k] === false) delete next[k];
    });
    onChange(next);
  };
  return (
    <div className="f-filters" role="group" aria-label={t('aria')}>
      <label>
        <span className="u-sr-only">{t('sprint')}</span>
        <select className={filters.sprintId ? 'm-chip-select m-chip-select--active' : 'm-chip-select'} value={filters.sprintId ?? ''} onChange={(e) => patch({ sprintId: e.target.value || undefined })}>
          <option value="">{t('activeSprint')}</option>
          {sprints.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="u-sr-only">{t('member')}</span>
        <select className={filters.assigneeId ? 'm-chip-select m-chip-select--active' : 'm-chip-select'} value={filters.assigneeId ?? ''} onChange={(e) => patch({ assigneeId: e.target.value || undefined })}>
          <option value="">{t('allMembers')}</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="u-sr-only">{t('tag')}</span>
        <select className={filters.tag ? 'm-chip-select m-chip-select--active' : 'm-chip-select'} value={filters.tag ?? ''} onChange={(e) => patch({ tag: e.target.value || undefined })}>
          <option value="">{t('allTags')}</option>
          {tags.map((tg) => (
            <option key={tg} value={tg}>
              {tg}
            </option>
          ))}
        </select>
      </label>
      <FilterChip label={t('timeLogged')} caret={false} active={!!filters.onlyLogged} onClick={() => patch({ onlyLogged: !filters.onlyLogged })} />
      <FilterChip label={t('varianceOnly')} caret={false} active={!!filters.onlyVariance} onClick={() => patch({ onlyVariance: !filters.onlyVariance })} />
    </div>
  );
}
